import { DataTypeInfo } from '@ariksa/data-scanning/api';
import { NativeResources } from '@ariksa/inventory-core/api';
import { startCase } from 'lodash';

const DATABASE_RESOURCE_TYPES = [
  NativeResources.RedshiftCluster,
  NativeResources.RelationalDatabaseService,
  NativeResources.DynamoDb,
  NativeResources.RdsCluster,
  NativeResources.DocumentDb,
  NativeResources.DocumentDbCluster,
  NativeResources.SnowflakeDatabases,
  NativeResources.SnowflakeTables,
];

export const isDatabaseResource = (resourceType: NativeResources) =>
  DATABASE_RESOURCE_TYPES.includes(resourceType);

export const REPOSITORY_RESOURCE_TYPES: NativeResources[] = [
  NativeResources.GitHubRepository,
  NativeResources.GitLabProject,
  NativeResources.BitBucketRepository,
];

export const isRepositoryResource = (resourceType: NativeResources) =>
  REPOSITORY_RESOURCE_TYPES.includes(resourceType);

export const getSensitivityRecordCount = (
  row: DataTypeInfo,
  resourceType: NativeResources,
) => {
  if (isRepositoryResource(resourceType)) return row?.count ?? 0;

  const rowCount = row?.row_count;
  if (rowCount == null) return 0;

  return rowCount * (row?.found_in_amount ?? 0);
};

export const getSensitiveDataBreakdown = (
  data: DataTypeInfo[] = [],
  resourceType: NativeResources,
) => {
  const categories: Record<string, number> = {};
  const sensitivities: Record<string, number> = {};

  data.forEach(row => {
    const count = getSensitivityRecordCount(row, resourceType);
    const category = row?.data_type || 'Unknown';
    const sensitivity = row?.sensitivity_category || 'Unknown';

    categories[category] = (categories[category] || 0) + count;
    sensitivities[sensitivity] = (sensitivities[sensitivity] || 0) + count;
  });

  return {
    categoryData: Object.entries(categories).map(([name, value]) => ({
      name,
      value,
    })),
    sensitivityData: Object.entries(sensitivities).map(([name, value]) => ({
      name,
      value,
    })),
  };
};

export const getStatusLabel = status => {
  if (!status) return '-';
  if (status.toLowerCase() === 'success') return 'SCANNED';
  return startCase(status).toUpperCase();
};

export const getLastScanTimestamp = (...resources: Record<string, any>[]) => {
  const timestampKeys = [
    'last_scanned_at',
    'last_scan_at',
    'last_scan_date',
    'last_scanned',
    'scanned_at',
    'scan_completed_at',
    'last_scan',
    'scan_date',
  ];

  return resources
    .flatMap(resource => [resource?.extra_config, resource?.config, resource])
    .map(resource => timestampKeys.map(key => resource?.[key]).find(Boolean))
    .find(Boolean);
};

export const getFoundInLabel = (resourceType, amount) => {
  const unit = DATABASE_RESOURCE_TYPES.includes(resourceType)
    ? 'column'
    : 'file';

  return `${amount} ${unit}${amount === 1 ? '' : 's'}`;
};

export const hideTooltipLabel = () => '';
